import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Flex, useToast } from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { getMyPartnerCode } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PageHeader, KpiStrip, KpiCard } from "../../../components/Portal/ui";
import PartnersPoolList from "./PartnersPoolList";
import PartnersCompanyList from "./PartnersCompanyList";

/**
 * ASAP Partners — Pools & Partner Companies (reference page / gold standard for
 * the Portal Design Rework). Owns the page header, the KPI strip (pool count,
 * partner count, partner code) and lays out the two list panels side by side.
 * The child lists report their totals up via onTotalChange so the strip stays
 * in sync without a duplicate fetch.
 */
const PartnersPoolsAndCompanies = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [poolTotal, setPoolTotal] = useState(null);
  const [partnerTotal, setPartnerTotal] = useState(null);
  const [partnerCode, setPartnerCode] = useState(null);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    let active = true;
    (async () => {
      try {
        const res = await getMyPartnerCode({ buId: selectedBusinessUnitId || undefined });
        if (active) setPartnerCode(res.partnerCode || null);
      } catch {
        if (active) setPartnerCode(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  const handleCopyCode = useCallback(async () => {
    if (!partnerCode) return;
    try {
      await navigator.clipboard.writeText(partnerCode);
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = partnerCode;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast({
      title: t("partners.myCode.copied", "Partner code copied to clipboard"),
      status: "info",
      duration: 2000,
    });
  }, [partnerCode, toast, t]);

  return (
    // Bleed past PortalLayout's p={6} so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="AP"
        eyebrow={t("partners.poolsAndPartners.eyebrow", "ASAP Partners")}
        title={t("partners.poolsAndPartners.title", "Pools & Partners")}
      />

      <Box p="20px">
        <KpiStrip columns={{ base: 1, sm: 2, lg: 3 }} mb="12px">
          <KpiCard
            label={t("partners.pools.title", "Consultant Pools")}
            value={poolTotal == null ? "—" : poolTotal}
          />
          <KpiCard
            label={t("partners.partnerLinks.title", "Partner Companies")}
            value={partnerTotal == null ? "—" : partnerTotal}
          />
          <KpiCard
            label={t("partners.myCode.title", "Your Partner Code")}
            value={partnerCode || "—"}
            code
            actions={
              partnerCode ? (
                <Button
                  variant="secondary"
                  size="xs"
                  leftIcon={<CopyIcon />}
                  onClick={handleCopyCode}
                >
                  {t("partners.myCode.copy", "Copy")}
                </Button>
              ) : null
            }
          />
        </KpiStrip>

        <Flex gap="12px" align="stretch" direction={{ base: "column", lg: "row" }}>
          <Box flex="1" minW={0} display="flex">
            <PartnersPoolList onTotalChange={setPoolTotal} />
          </Box>
          <Box flex="1" minW={0} display="flex">
            <PartnersCompanyList onTotalChange={setPartnerTotal} />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default PartnersPoolsAndCompanies;
